import { Link } from 'react-router-dom'
import { Trash2, Minus, Plus } from 'lucide-react'
import { formatVND } from '@/lib/formatters'
import type { CartItem } from '../../model/types'
import { useCartStore } from '../../state/cartStore'

interface CartItemRowProps {
  item: CartItem
  compact?: boolean
}

export function CartItemRow({ item, compact = false }: CartItemRowProps) {
  const increment = useCartStore((s) => s.increment)
  const decrement = useCartStore((s) => s.decrement)
  const removeItem = useCartStore((s) => s.removeItem)

  const lineTotal = item.price * item.qty

  return (
    <div className="flex gap-3 py-4 border-b border-border last:border-b-0">
      <Link
        to={`/san-pham/${item.slug}`}
        className={[
          'shrink-0 rounded-md overflow-hidden bg-slate-100 border border-border',
          compact ? 'w-16 h-16' : 'w-20 h-20',
        ].join(' ')}
      >
        <img
          src={item.image}
          alt={item.name}
          className="w-full h-full object-cover"
          onError={(e) => {
            (e.target as HTMLImageElement).src =
              'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><rect width="1" height="1" fill="%23e2e8f0"/></svg>'
          }}
        />
      </Link>

      <div className="flex-1 min-w-0">
        <div className="text-[11px] text-muted uppercase tracking-wide">{item.brand}</div>
        <Link
          to={`/san-pham/${item.slug}`}
          className="text-sm font-medium text-text hover:text-brand-600 line-clamp-2 leading-snug"
        >
          {item.name}
        </Link>
        <div className="mt-1 flex items-center gap-2">
          <span className="text-brand-600 font-bold text-sm">{formatVND(item.price)}</span>
          {item.oldPrice > item.price && (
            <span className="text-muted text-xs line-through">{formatVND(item.oldPrice)}</span>
          )}
        </div>

        <div className="mt-2 flex items-center justify-between">
          <div className="inline-flex items-center border border-border rounded-md overflow-hidden">
            <button
              onClick={() => decrement(item.id)}
              className="w-7 h-7 flex items-center justify-center hover:bg-brand-50"
              aria-label="Giảm"
            >
              <Minus className="w-3.5 h-3.5" />
            </button>
            <span className="w-8 h-7 flex items-center justify-center text-sm font-medium">{item.qty}</span>
            <button
              onClick={() => increment(item.id)}
              className="w-7 h-7 flex items-center justify-center hover:bg-brand-50"
              aria-label="Tăng"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="flex items-center gap-3">
            {!compact && (
              <span className="text-brand-700 font-bold text-sm">{formatVND(lineTotal)}</span>
            )}
            <button
              onClick={() => removeItem(item.id)}
              className="w-7 h-7 flex items-center justify-center text-muted hover:text-danger transition"
              aria-label="Xóa"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
